import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import type { MarketCountry } from "@/config/market";

// All copy ships in the bundle. Editing a string means editing these files and
// redeploying — there is no translation service at runtime.
import en from "@/locales/en.json";
import enNL from "@/locales/en-NL.json";
import enDE from "@/locales/en-DE.json";
import de from "@/locales/de.json";
import fr from "@/locales/fr.json";
import nl from "@/locales/nl.json";

// Namespace for booking app translations
export const BOOKING_NS = "booking_flow";

// Supported languages
export const supportedLanguages = ["en", "de", "fr", "nl"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

// Map BookingLanguage to i18next language codes
export const languageMap: Record<string, SupportedLanguage> = {
  "en-US": "en",
  "de-DE": "de",
  "fr-FR": "fr",
  "nl-NL": "nl",
  en: "en",
  de: "de",
  fr: "fr",
  nl: "nl",
};

/**
 * English is not one locale: the Netherlands and Germany each have their own
 * full English file, even where the copy is still identical, so a market's
 * English can diverge without touching the others (en-NL already says "70+"
 * biomarkers where the Swiss copy says "80+").
 *
 * i18next still sees a single "en" — call sites pass `languageMap[language]`
 * and several of them feed that code to Intl and chat widgets, where "en-NL"
 * would change formatting. So instead of a new language code, the store calls
 * this whenever the market changes and the right file fills "en";
 * react-i18next re-renders on the swap (`bindI18nStore: "added"` below).
 */
const ENGLISH_BY_MARKET: Record<MarketCountry, typeof en> = {
  ch: en,
  at: en,
  de: enDE,
  nl: enNL,
};

export const applyMarketEnglish = (market: MarketCountry): void => {
  i18next.addResourceBundle(
    "en",
    BOOKING_NS,
    ENGLISH_BY_MARKET[market],
    true,
    true,
  );
};

export const i18nPromise = i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      // A copy, not the import: i18next keeps this object as its live store and
      // applyMarketEnglish writes into it, which would otherwise overwrite the
      // Swiss copy in `en` itself and make a switch back to CH impossible.
      en: { [BOOKING_NS]: { ...en } },
      de: { [BOOKING_NS]: de },
      fr: { [BOOKING_NS]: fr },
      nl: { [BOOKING_NS]: nl },
    },
    fallbackLng: "en",
    supportedLngs: [...supportedLanguages],
    ns: [BOOKING_NS],
    defaultNS: BOOKING_NS,
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "locale",
      lookupLocalStorage: "userLanguage",
      // Don't cache: i18next would write the 2-letter code ("de") to
      // "userLanguage", but the store/useLanguage own that key with the full
      // BookingLanguage ("de-DE"). detectInitialLanguage strict-equals "de-DE",
      // so an i18next cache write silently drops the saved preference on reload.
      caches: [],
      convertDetectedLanguage: (lng: string) => {
        const resolved = languageMap[lng] || lng.split("-")[0];
        // French is Switzerland-only: never start a German-speaking market
        // (?country=de / ?country=at) in French at i18n init, so the UI doesn't
        // flash FR before the store coerces it. Kept inline (no market.ts
        // import) to avoid a config cycle.
        if (
          resolved === "fr" &&
          typeof window !== "undefined" &&
          ["de", "at"].includes(
            new URLSearchParams(window.location.search).get("country") ?? "",
          )
        ) {
          return "de";
        }
        return resolved;
      },
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      // Re-render when a market swaps its English file in (applyMarketEnglish).
      bindI18nStore: "added",
    },
  });

export default i18next;
